import type { ReactNode } from 'react';
import { CircleSlash, Download, HelpCircle } from 'lucide-react';
import type { Indicador, RecorteDoRelatorio } from '@central/contracts';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { baixarCsv, nomeDeArquivo, paraCsv, type ColunaDeCsv } from '@/features/relatorios/csv';
import { fimDoRecorte, inicioDoRecorte } from '@/features/relatorios/periodo';

/**
 * As peças que todas as abas de relatório compartilham.
 *
 * Duas regras estão codificadas aqui, e não na disciplina de quem escreve cada
 * tela:
 *
 * **Todo gráfico vem com a tabela ao lado.** `Painel` recebe os dois e desenha
 * os dois — não há como usar este componente e esquecer a tabela. Gráfico
 * bonito sem o número atrás não serve para levar a reunião: ninguém lê "247"
 * de uma barra.
 *
 * **Indicador sem valor não vira zero.** `Painel` e `Numero` recebem o
 * `Indicador<T>` inteiro e desenham o estado: com valor, sem dado, ou não
 * calculável — este último com o que falta escrito na tela, porque é uma
 * informação acionável e não um erro.
 */

// ── estados de um indicador ────────────────────────────────────────────────

function SemDado({ motivo }: { motivo: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-10 text-center">
      <CircleSlash className="h-6 w-6 text-muted-foreground/70" aria-hidden />
      <p className="text-sm font-medium">Sem dado no período</p>
      <p className="mx-auto max-w-md text-sm text-muted-foreground">{motivo}</p>
    </div>
  );
}

function NaoCalculavel({ motivo, falta }: { motivo: string; falta: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-warning/40 bg-warning/5 px-6 py-10 text-center">
      <HelpCircle className="h-6 w-6 text-warning" aria-hidden />
      <p className="text-sm font-medium text-warning">Não é possível calcular</p>
      <p className="mx-auto max-w-md text-sm text-muted-foreground">{motivo}</p>
      <p className="mx-auto max-w-md text-xs text-muted-foreground">
        <span className="font-medium">O que falta:</span> {falta}
      </p>
    </div>
  );
}

/**
 * Deriva um indicador de outro, preservando o motivo da ausência.
 *
 * A tela costuma querer um pedaço do que o servidor mandou — a aderência de
 * dentro do resumo de ANS, por exemplo. Recalcular o `Indicador` à mão em cada
 * lugar é onde o "sem dado" vira `0`; aqui a ausência atravessa a derivação, e
 * `null` do extrator vira "não calculável" com o motivo que quem chamou
 * escreveu.
 */
export function derivar<A, B>(
  origem: Indicador<A>,
  extrair: (valor: A) => B | null,
  quandoNulo: { motivo: string; falta: string },
): Indicador<B> {
  if (origem.estado !== 'OK') return origem;
  const derivado = extrair(origem.valor);
  if (derivado === null) return { estado: 'NAO_CALCULAVEL', ...quandoNulo };
  return { estado: 'OK', valor: derivado, amostra: origem.amostra };
}

/** Desenha o conteúdo quando há valor, e o motivo quando não há. */
export function ComIndicador<T>({
  indicador,
  children,
}: {
  indicador: Indicador<T>;
  children: (valor: T, amostra: number) => ReactNode;
}) {
  if (indicador.estado === 'SEM_DADO') return <SemDado motivo={indicador.motivo} />;
  if (indicador.estado === 'NAO_CALCULAVEL') {
    return <NaoCalculavel motivo={indicador.motivo} falta={indicador.falta} />;
  }
  return <>{children(indicador.valor, indicador.amostra)}</>;
}

// ── tabela ─────────────────────────────────────────────────────────────────

export interface ColunaDeTabela<L> {
  cabecalho: string;
  /** O que aparece na célula. */
  celula: (linha: L) => ReactNode;
  /** O que vai no CSV. Sem isto, cai no texto da célula quando ele é escalar. */
  csv?: (linha: L) => string | number | null | undefined;
  alinharADireita?: boolean;
}

export function Tabela<L>({
  colunas,
  linhas,
  chaveDe,
  rodape,
}: {
  colunas: ReadonlyArray<ColunaDeTabela<L>>;
  linhas: readonly L[];
  chaveDe: (linha: L, indice: number) => string;
  rodape?: ReactNode;
}) {
  return (
    /*
     * A tabela rola sozinha, nos dois eixos.
     *
     * Na horizontal, porque sem isto uma coluna longa empurraria a página
     * inteira para o lado e o gráfico ao lado sairia da tela. Na vertical,
     * porque uma série de sessenta dias tem sessenta linhas: sem o teto, o
     * painel ficaria com dois metros de altura e um vazio enorme ao lado do
     * gráfico, que tem altura fixa. O cabeçalho gruda no topo para a rolagem
     * não perder o nome da coluna.
     */
    <div className="max-h-64 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            {colunas.map((coluna) => (
              <th
                key={coluna.cabecalho}
                scope="col"
                // `whitespace-nowrap` no cabeçalho: metade da tabela vive ao
                // lado de um gráfico, num espaço estreito, e sem isto
                // "Participação" quebra ou é cortada no meio da palavra — o que
                // parece defeito. Cortada com barra de rolagem parece o que é.
                className={cn(
                  'whitespace-nowrap px-2 py-2 font-medium',
                  coluna.alinharADireita && 'text-right',
                )}
              >
                {coluna.cabecalho}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, indice) => (
            <tr key={chaveDe(linha, indice)} className="border-b border-border/60 last:border-0">
              {colunas.map((coluna) => (
                <td
                  key={coluna.cabecalho}
                  className={cn(
                    'px-2 py-1.5',
                    coluna.alinharADireita && 'text-right font-mono tabular-nums',
                  )}
                >
                  {coluna.celula(linha)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {rodape ? <tfoot className="border-t border-border font-medium">{rodape}</tfoot> : null}
      </table>
    </div>
  );
}

// ── painel: gráfico + tabela + exportação ──────────────────────────────────

function colunasDeCsv<L>(colunas: ReadonlyArray<ColunaDeTabela<L>>): Array<ColunaDeCsv<L>> {
  return colunas.map((coluna) => ({
    cabecalho: coluna.cabecalho,
    valor: (linha: L) => {
      if (coluna.csv) return coluna.csv(linha);
      const conteudo = coluna.celula(linha);
      // Célula que devolve JSX não tem representação textual óbvia; declarar
      // `csv` é obrigação de quem a escreveu, e o traço deixa a falta visível
      // no arquivo em vez de inventar um valor.
      return typeof conteudo === 'string' || typeof conteudo === 'number' ? conteudo : '—';
    },
  }));
}

/**
 * Um indicador com forma de lista: gráfico à esquerda, tabela à direita.
 *
 * Em telas estreitas os dois empilham, com a tabela **embaixo** — a ordem
 * importa porque a tabela é a versão acessível do gráfico, e um leitor de tela
 * percorre na ordem do documento.
 */
export function Painel<L>({
  titulo,
  descricao,
  indicador,
  grafico,
  colunas,
  chaveDe,
  nome,
  recorte,
}: {
  titulo: string;
  descricao?: string;
  indicador: Indicador<L[]>;
  grafico: (linhas: L[]) => ReactNode;
  /**
   * Recebe as linhas em vez de ser uma lista pronta: colunas de participação
   * precisam do total, e calcular o total fora obrigaria cada tela a
   * desembrulhar o `Indicador` à mão — que é exatamente o desembrulho que este
   * componente existe para evitar.
   */
  colunas: (linhas: L[]) => ReadonlyArray<ColunaDeTabela<L>>;
  chaveDe: (linha: L, indice: number) => string;
  /** Partes do nome do arquivo exportado. */
  nome: readonly string[];
  recorte?: RecorteDoRelatorio;
}) {
  const podeExportar = indicador.estado === 'OK' && indicador.valor.length > 0;

  return (
    <Card>
      <CardHeader
        title={titulo}
        {...(descricao === undefined ? {} : { description: descricao })}
        action={
          podeExportar ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<Download className="h-3.5 w-3.5" aria-hidden />}
              onClick={() => {
                const linhas = indicador.estado === 'OK' ? indicador.valor : [];
                baixarCsv(nomeDeArquivo(nome), paraCsv(colunasDeCsv(colunas(linhas)), linhas));
              }}
            >
              CSV
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        <ComIndicador indicador={indicador}>
          {(linhas) => (
            <div className="grid gap-6 lg:grid-cols-2">
              {grafico(linhas)}
              <Tabela colunas={colunas(linhas)} linhas={linhas} chaveDe={chaveDe} />
            </div>
          )}
        </ComIndicador>
        {recorte?.recortadoPorEscopo ? <AvisoDeEscopo /> : null}
      </CardBody>
    </Card>
  );
}

// ── números soltos ─────────────────────────────────────────────────────────

/**
 * Um número em destaque.
 *
 * Recebe o `Indicador` inteiro, e não o número: é aqui que "0" e "não sei"
 * seriam confundidos, porque um azulejo grande com um zero no meio é lido como
 * fato antes de ser lido como ausência.
 */
export function Numero({
  rotulo,
  indicador,
  formatar,
  tom = 'neutro',
  ajuda,
}: {
  rotulo: string;
  indicador: Indicador<number>;
  formatar: (valor: number) => string;
  tom?: 'neutro' | 'bom' | 'atencao' | 'ruim';
  ajuda?: string;
}) {
  const cor = {
    neutro: 'text-foreground',
    bom: 'text-success',
    atencao: 'text-warning',
    ruim: 'text-destructive',
  }[tom];

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-card">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{rotulo}</p>
      {indicador.estado === 'OK' ? (
        <p className={cn('mt-1 text-2xl font-semibold tabular-nums', cor)}>
          {formatar(indicador.valor)}
        </p>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground" title={indicador.motivo}>
          {indicador.estado === 'SEM_DADO' ? 'sem dado' : 'não calculável'}
        </p>
      )}
      {ajuda ? <p className="mt-1 text-xs text-muted-foreground">{ajuda}</p> : null}
    </div>
  );
}

/** Contagem simples, sempre calculável — não passa pelo `Indicador`. */
export function Contagem({
  rotulo,
  valor,
  tom = 'neutro',
  ajuda,
}: {
  rotulo: string;
  valor: number;
  tom?: 'neutro' | 'bom' | 'atencao' | 'ruim';
  ajuda?: string;
}) {
  return (
    <Numero
      rotulo={rotulo}
      indicador={{ estado: 'OK', valor, amostra: valor }}
      formatar={(n) => n.toLocaleString('pt-BR')}
      tom={tom}
      {...(ajuda === undefined ? {} : { ajuda })}
    />
  );
}

// ── moldura de cada aba ────────────────────────────────────────────────────

function AvisoDeEscopo() {
  return (
    <p className="mt-4 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-muted-foreground">
      Estes números estão recortados ao seu escopo de acesso. Eles são corretos para você e
      menores que o total da Central — dois gestores com escopos diferentes veem números
      diferentes de propósito.
    </p>
  );
}

/**
 * Cabeçalho de recorte: o que está sendo contado, escrito por extenso.
 *
 * As duas datas saem no fuso que a resposta declara, e o fim é o **último dia
 * contado** — `recorte.ate` é exclusiva, e imprimi-la nomearia um dia que não
 * entrou na conta.
 */
export function ResumoDoRecorte({ recorte }: { recorte: RecorteDoRelatorio }) {
  return (
    <p className="text-xs text-muted-foreground">
      Período de {inicioDoRecorte(recorte.de, recorte.fusoHorario)} a{' '}
      {fimDoRecorte(recorte.ate, recorte.fusoHorario)} (os dois dias inteiros) · baldes por{' '}
      {{ dia: 'dia', semana: 'semana', mes: 'mês' }[recorte.granularidade]}{' '}
      no fuso {recorte.fusoHorario}
      {recorte.recortadoPorEscopo ? ' · recortado ao seu escopo de acesso' : ''}
    </p>
  );
}

/** Espera e falha, iguais em todas as abas. */
export function Carregando() {
  return (
    <SkeletonRegion label="Carregando indicadores" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <Skeleton className="h-72" />
      <Skeleton className="h-72" />
    </SkeletonRegion>
  );
}

export function Falhou({ erro, aoTentar }: { erro: unknown; aoTentar: () => void }) {
  return <ErrorState error={erro} onRetry={aoTentar} title="Não foi possível montar o relatório" />;
}
